/** Dependency-free date helpers (native `Date`) shared by Calendar / DatePicker. */

export interface DateRange {
  from: Date | null;
  to: Date | null;
}

export const DAYS_IN_WEEK = 7;
export const WEEKS_SHOWN = 6;
const MONTHS_IN_YEAR = 12;

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function isSameDay(a: Date | null, b: Date | null): boolean {
  if (a == null || b == null) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

export function addDays(date: Date, amount: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

export function addMonths(date: Date, amount: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth() + amount, 1);
  return next;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** Inclusive day count from `a` to `b`, ignoring time. */
export function isBefore(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() < startOfDay(b).getTime();
}

export function isAfter(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() > startOfDay(b).getTime();
}

export function isWithin(date: Date, from: Date, to: Date): boolean {
  const t = startOfDay(date).getTime();
  const lo = Math.min(startOfDay(from).getTime(), startOfDay(to).getTime());
  const hi = Math.max(startOfDay(from).getTime(), startOfDay(to).getTime());
  return t >= lo && t <= hi;
}

export function clampDate(date: Date, min: Date | null, max: Date | null): Date {
  if (min != null && isBefore(date, min)) return startOfDay(min);
  if (max != null && isAfter(date, max)) return startOfDay(max);
  return startOfDay(date);
}

/**
 * The 42 cells (6 weeks) covering `month`, starting on the week that contains the 1st.
 * `weekStartsOn` 0 = Sunday, 1 = Monday.
 */
export function buildMonthGrid(month: Date, weekStartsOn: 0 | 1): Date[] {
  const first = startOfMonth(month);
  const lead = (first.getDay() - weekStartsOn + DAYS_IN_WEEK) % DAYS_IN_WEEK;
  const gridStart = addDays(first, -lead);
  return Array.from({ length: WEEKS_SHOWN * DAYS_IN_WEEK }, (_, i) => addDays(gridStart, i));
}

/** Weekday label order for a given week start (indexes into a Sunday-first label array). */
export function weekdayOrder(weekStartsOn: 0 | 1): number[] {
  return Array.from({ length: DAYS_IN_WEEK }, (_, i) => (i + weekStartsOn) % DAYS_IN_WEEK);
}

export const DEFAULT_MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Sunday-first, two-letter weekday labels (matching the Figma Mo/Tu/We…). */
export const DEFAULT_WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const DEFAULT_WEEKDAY_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export function monthTitle(month: Date, monthLabels: string[]): string {
  return `${monthLabels[month.getMonth()] ?? ''} ${String(month.getFullYear())}`;
}

export { MONTHS_IN_YEAR };
